import {
  emptyId,
  FiltersToolbar,
  FlowStatusEnum,
  getFlowFilters,
  getFlowProperties,
  HeaderWithScrollables,
  localStorageKeyPerformancePageFiltersValue,
  localStorageKeyPerformancePageProperties,
  normalizeFiltersValues,
  PageLayout,
  PageNames,
  performanceDefaultQuickFilters,
  SyncFiltersPropertiesProvider,
  useEnvironment,
  useExportGridButton,
  useFilters,
  useLocationTrackingForPerformanceReport,
  useDataExplorerReportStore,
  useWorkflows,
  useGetFiltersToolbarHeight,
  DATA_EXPLORER,
  useTrackRecentlyViewed,
  HomePageItemDataType,
  DATA_EXPLORER_ID,
  usePerformanceGraphQLExportParameters,
  useSidePeekDrawerProps,
} from '@diana/core';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Fragment, useEffect, useMemo } from 'react';
import { useBreadcrumbsRoot } from '../../hooks/useBreadcrumbsRoot';
import { Content } from './Content';
import { HeaderLeft, HeaderRight } from './Headers';
import { createPropertyToFilter } from './Support';

export function DataExplorerReport() {
  const environment = useEnvironment();
  const gridApiRef = useGridApiRef();
  const sidePeekDrawerProps = useSidePeekDrawerProps();
  const { isFetching: isFetchingFlows, data: flows } = useWorkflows({
    flowStatuses: [FlowStatusEnum.Active, FlowStatusEnum.Archived],
  });

  useTrackRecentlyViewed({ itemId: DATA_EXPLORER_ID, dataType: HomePageItemDataType.Report });

  useLocationTrackingForPerformanceReport(
    { layout: { id: emptyId, displayName: 'Data Explorer' } },
    { trackOnMount: true },
  );

  const setFiltersValues = useDataExplorerReportStore((store) => store.setFiltersValues);
  const setFilters = useDataExplorerReportStore((store) => store.setFilters);
  const setFlows = useDataExplorerReportStore((store) => store.setFlows);
  const setAvailableFilters = useDataExplorerReportStore((store) => store.setAvailableFilters);
  const setDefaults = useDataExplorerReportStore((store) => store.setDefaults);
  const filters = useDataExplorerReportStore((store) => store.filters);
  const availableFilters = useDataExplorerReportStore((store) => store.availableFilters);
  const filtersValues = useDataExplorerReportStore((store) => store.filtersValues);
  const exportConfigurations = useDataExplorerReportStore((store) => store.exportConfigurations);
  const defaultFiltersValuesAndPropertiesSet = useDataExplorerReportStore(
    (store) => store.defaultFiltersValuesAndPropertiesSet,
  );

  const queryParameters = usePerformanceGraphQLExportParameters({
    flows: flows ?? [],
    filters: availableFilters,
    filtersValues,
    showCompleted: true,
    showDeleted: true,
    showInProgress: true,
  });

  const { isExportModalOpen, setIsExportModalOpen, ExportModalComponent } = useExportGridButton({
    apiRef: gridApiRef,
    params: queryParameters,
    exportConfigurations,
  });

  const propertyToFilter = useMemo(createPropertyToFilter, []);

  const supportedProperties = useMemo(() => {
    return flows
      ? getFlowProperties({
          flow: flows,
          onlyEnabled: false,
          onlyDefaultFilter: false,
        })
      : [];
  }, [flows]);

  useEffect(() => {
    if (!flows || !flows.length) return;
    const availableFilters = getFlowFilters({
      flow: flows,
      propertyToFilter,
      onlyDefaultFilter: false,
      onlyEnabled: false,
    });

    setFlows(flows);
    setAvailableFilters(availableFilters);
    // eslint-disable-next-line
  }, [flows]);

  const { defaultData, onFilterChange, needToRenderAIFilter } = useFilters({
    flows: flows ? flows : [],
    createPropertyToFilter,
    filtersValues,
    setFiltersValues,
    availableFilters,
    defaultFiltersValues: normalizeFiltersValues({
      filtersValues: performanceDefaultQuickFilters,
      availableFilters,
    }),
    withDefaultFiltersValuesFromFlow: false,
  });

  const breadcrumbRoot = useBreadcrumbsRoot('performance');

  const height = useGetFiltersToolbarHeight(true);

  return (
    <SyncFiltersPropertiesProvider
      filtersKey={localStorageKeyPerformancePageFiltersValue(environment)}
      propertiesKey={localStorageKeyPerformancePageProperties(environment)}
      subscribe={useDataExplorerReportStore.subscribe}
      storeSetDefaults={setDefaults}
      defaultFiltersValuesAndPropertiesSet={
        defaultFiltersValuesAndPropertiesSet || flows?.length === 0 || (!isFetchingFlows && !flows)
      }
    >
      <PageLayout
        name={PageNames.PERFORMANCE}
        title={breadcrumbRoot.title}
        isContentLoading={isFetchingFlows}
        filters={
          (!isFetchingFlows && !flows) || flows?.length === 0 ? undefined : (
            <FiltersToolbar
              availableFilters={availableFilters}
              setAvailableFilters={setAvailableFilters}
              filters={filters}
              setFilters={setFilters}
              filtersValues={filtersValues}
              setFiltersValues={setFiltersValues}
              needToRenderAIFilter={needToRenderAIFilter}
              height={height}
              onFilterChange={onFilterChange}
              defaultData={defaultData}
              needToShowManageDashboardButtons
              isContentLoading={isFetchingFlows}
              reportId={DATA_EXPLORER}
              gridApiRef={gridApiRef}
            />
          )
        }
        rightDrawer={sidePeekDrawerProps}
        header={
          <HeaderWithScrollables
            left={<HeaderLeft />}
            right={flows ? <HeaderRight setIsExportModalOpen={setIsExportModalOpen} /> : null}
          />
        }
      >
        {flows ? (
          <Fragment>
            <Content
              ref={gridApiRef}
              flows={flows}
              forceEmptySelection={isExportModalOpen}
              supportedProperties={supportedProperties || []}
            />
            {ExportModalComponent}
          </Fragment>
        ) : null}
      </PageLayout>
    </SyncFiltersPropertiesProvider>
  );
}
